import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import FormGroup from '../../../../../../components/global/FormGroup';
import Alert from '../../../../../../components/global/Alert';
import Button from '../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../../../util/sentry';
import PriceDisplay from '../../../../../../components/global/PriceDisplay';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../../../constants/account-referrals';
import PromoCodeInput from '../../../../../../components/global/PromoCodeInput/PromoCodeInput';
import { checkoutPrice as checkoutPriceQuery } from '../../../../../../graphql/queries/checkout';
import useVirtualPageView from '../../../../../pro/services/analytics/useVirtualPageView';
import { gtm } from '../../../../../../util/analytics';

const propTypes = {
  className: PropTypes.string,
  values: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    promo_code: PropTypes.string,
    default_promo_code: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onCheckout: PropTypes.func.isRequired,
  eventItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.shape({
      value: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#969ea0',
      color: '#3c4c51',
      fontWeight: 800,
      fontFamily: 'Muli, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#969ea0',
        fontWeight: 800,
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const RegistrationPayment = ({
  className,
  values,
  submitting,
  onCheckout,
  handleChange,
  eventItem,
}) => {
  const classes = classNames({
    [className]: !!className,
    'view-register-checkout-form': true,
  });

  useVirtualPageView({
    title: 'Reserve Waldo for your wedding today! - Payment',
    path: '/payment',
  });

  useEffect(() => {
    // Track checkout initiation
    gtm.trackEvent({
      type: 'initiatecheckout',
    });
  }, []);

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');
  const [name, setName] = useState(
    `${values.first_name} ${values.last_name}`.trim(),
  );
  const checkPromoCodeRef = useRef();
  const [shouldCheckPromoCode, setShouldCheckPromoCode] = useState(false);
  const [email, setEmail] = useState(values?.email_address ?? '');
  const { data: { checkoutPrice } = {}, loading: loadingPricing } = useQuery(
    checkoutPriceQuery,
    {
      variables: {
        event_items: [
          {
            event_item_id: eventItem.id,
            quantity: 1,
          },
        ],
        promo_code: values.promo_code,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      skip: !eventItem,
    },
  );

  const isFreeCheckout = checkoutPrice?.total_price?.value === 0;

  const handleSubmit = async event => {
    event.preventDefault();

    if (loading || submitting) {
      return;
    }

    setLoading(true);
    setStripeError('');
    try {
      if (shouldCheckPromoCode && checkPromoCodeRef.current) {
        const success = await checkPromoCodeRef.current();
        if (!success) {
          setLoading(false);
          setStripeError('');
          return;
        }
      }
      const { error, token } = isFreeCheckout
        ? {}
        : await stripe.createToken(elements.getElement(CardElement), {
            name,
            email,
          });

      if (error) {
        throw Error(error.message);
      }

      handleChange({
        target: {
          name: 'eventItemId',
          value: eventItem.id,
        },
      });

      handleChange({
        target: {
          name: 'email',
          value: email,
        },
      });

      handleChange({
        target: {
          name: 'stripe_token',
          value: token,
        },
      });

      handleChange({
        target: {
          name: 'is_free_checkout',
          value: isFreeCheckout,
        },
      });

      onCheckout();
    } catch (error) {
      setStripeError(getErrorString(error));
      sentryCaptureMessage('WEDDINGS SIGNUP - CC Capture Error ', {
        level: 'error',
        extra: {
          error,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <h4 className="view-register-form-title mb-2">Enter payment details</h4>
      <fieldset className="view-register-checkout-fieldset">
        <FormGroup
          label="Name"
          labelSrOnly
          id="card-name"
          name="name"
          placeholder="Name on card"
          required
          className="mb-1"
          value={name}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            setName(value);
          }}
          disabled={submitting || loading}
        />

        <FormGroup
          label="Email"
          labelSrOnly
          id="card-email"
          type="email"
          name="email"
          placeholder="Your email"
          required
          value={email}
          onChange={event => {
            const { target } = event;
            const { value } = target;
            setEmail(value);
          }}
          disabled={submitting || loading}
          className="mb-1"
        />

        <PromoCodeInput
          accountReferralTypes={[
            ACCOUNT_REFERRAL_TYPE.WEDDINGS_50_USD_OFF,
            ACCOUNT_REFERRAL_TYPE.WEDDINGS_100_PC_OFF_ONCE,
            ACCOUNT_REFERRAL_TYPE.WEDDINGS,
          ]}
          className="view-register-promo-box"
          labelSrOnly
          placeholder="Have a Promo Code?"
          onPromoCodeChange={code =>
            handleChange({
              target: {
                name: 'promo_code',
                value: code,
              },
            })
          }
          verifyBillingPeriod={false}
          defaultPromoCode={values.promo_code || values.default_promo_code}
          onDefaultPromoCodeParsed={() => {
            handleChange({
              target: {
                name: 'default_promo_code',
                value: '',
              },
            });
          }}
          onDismiss={() => {
            handleChange({
              target: {
                name: 'promo_code',
                value: '',
              },
            });
          }}
          onPromoCodeUpdate={(newPromoCode, isApplied, checkPromoCode) => {
            if (newPromoCode && !isApplied) {
              setShouldCheckPromoCode(true);
            } else {
              setShouldCheckPromoCode(false);
            }
            checkPromoCodeRef.current = checkPromoCode;
          }}
        />
      </fieldset>
      {!isFreeCheckout && (
        <fieldset className="view-register-checkout-fieldset view-register-checkout-fieldset-credit-card">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            disabled={submitting || loading}
          />
        </fieldset>
      )}

      {stripeError && (
        <Alert type="danger" className="mt-3">
          {stripeError}
        </Alert>
      )}

      <div className="text-center mt-3">
        <Button
          id="weddings-reg-btn-form-submit"
          type="submit"
          className="view-register-cta"
          disabled={submitting || loading || loadingPricing}
        >
          {submitting || loading || loadingPricing ? (
            'Processing...'
          ) : (
            <>
              {!isFreeCheckout ? 'Purchase' : 'Get'} Waldo For&nbsp;
              {!isFreeCheckout ? (
                <PriceDisplay
                  price={checkoutPrice?.total_price?.value}
                  currency={checkoutPrice?.total_price?.currency}
                />
              ) : (
                'FREE'
              )}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

RegistrationPayment.propTypes = propTypes;

export default RegistrationPayment;
